import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Wrench, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Missing reset token. Please request a new reset link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/a9aa23d080e21c73e35c372654eb037c02db153c7a092318b7c9bfe07c16643a.png"
          alt="Workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex flex-col items-start justify-end p-12">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench size={20} className="text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-xl uppercase tracking-tighter text-white leading-none">M N M TOOLS</div>
              <div className="text-primary text-xs font-body tracking-widest uppercase">Tools Hub</div>
            </div>
          </Link>
          <h2 className="font-heading font-black uppercase text-5xl text-white leading-none mb-4">
            Secure Your<br /><span className="text-primary">Account</span>
          </h2>
          <p className="text-white/60 text-base max-w-sm">
            Set a strong new password to keep your Tools Hub account safe.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-primary flex items-center justify-center">
            <Wrench size={18} className="text-black" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-black text-lg uppercase tracking-tighter text-white">M N M TOOLS</div>
        </Link>

        <div className="w-full max-w-sm">
          <Link to="/auth" className="flex items-center gap-1 text-muted-foreground hover:text-primary text-xs uppercase tracking-widest mb-6 transition-colors">
            <ArrowLeft size={12} /> Back to Login
          </Link>

          {done ? (
            /* Success state */
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-14 h-14 bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={26} className="text-green-400" />
              </div>
              <h1 className="font-heading font-black text-2xl uppercase text-white mb-3">Password Updated!</h1>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </button>
            </div>
          ) : error && !token ? (
            /* Invalid token state */
            <div className="text-center py-4" data-testid="reset-error">
              <div className="w-14 h-14 bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <XCircle size={26} className="text-red-400" />
              </div>
              <h1 className="font-heading font-black text-2xl uppercase text-white mb-3">Invalid Link</h1>
              <p className="text-muted-foreground text-sm mb-6">{error}</p>
              <button
                onClick={() => navigate('/auth')}
                className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm"
              >
                Request New Link
              </button>
            </div>
          ) : (
            /* Reset form */
            <>
              <h1 className="font-heading font-black text-3xl uppercase text-white mb-1">Set New Password</h1>
              <p className="text-muted-foreground text-sm mb-8">Choose a strong password for your account.</p>

              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 p-3 mb-4" data-testid="reset-form-error">
                  <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">New Password *</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 pr-11 py-3 outline-none text-sm transition-colors"
                      style={{ borderRadius: 0 }}
                      data-testid="new-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-0 top-0 bottom-0 px-3 text-white/30 hover:text-primary transition-colors"
                    >
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Confirm Password *</label>
                  <input
                    type={showPass ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                    style={{ borderRadius: 0 }}
                    data-testid="confirm-password-input"
                    required
                  />
                  {confirm && password !== confirm && (
                    <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !confirm}
                  className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm disabled:opacity-60 mt-2"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
